import {
	Component,
	OnDestroy,
	ViewEncapsulation
} from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Options } from 'sortablejs';
import { MatButtonModule } from '@angular/material/button';
import { SortablejsModule } from '@dustfoundation/ngx-sortablejs';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgClass, NgFor, NgSwitch, NgSwitchCase } from '@angular/common';

import { User } from '../../models/user';
import { UserService } from '../../services/user.service';
import { LocationService } from '../../services/location.service';
import { pageTransitions } from '../../animations/page-transitions';
import { Widget, WidgetType } from '../../models/widget';
import { DashboardWidgetService } from '../../services/dashboard-widget.service';
import { LoadingSpinnerComponent } from '../utils/loading-spinner.component';
import { OfflineComponent } from '../offline/offline.component';
import { OrderDashboardComponent } from '../../../order/components/order-dashboard.component';
import { NewsAlertComponent } from '../news-alert/news-alert.component';
import { BlockedServicesAlertComponent } from '../blocked-services-alert/blocked-services-alert.component';

import { WidgetAddComponent, DialogData } from './widget-add.component';
import { RtsWidgetComponent } from './widgets/rts-widget/rts-widget.component';
import { IssueFollowUpWidgetComponent } from './widgets/issue-widget/issue-follow-up-widget.component';
import { BiggestStocksWidgetComponent } from './widgets/biggest-stocks-widget/biggest-stocks-widget.component';
import { NewspWidgetComponent } from './widgets/news-widget/news-widget.component';
import { DashboardMyserviceComponent } from './dashboard-myservice.component';

@Component({
	selector: 'copo-dashboard',
	templateUrl: './dashboard.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	styleUrl: './dashboard.component.css',
	imports: [
		NgIf,
		NewsAlertComponent,
		BlockedServicesAlertComponent,
		OrderDashboardComponent,
		DashboardMyserviceComponent,
		NewspWidgetComponent,
		TranslateModule,
		MatRippleModule,
		NgClass,
		MatIconModule,
		SortablejsModule,
		NgFor,
		NgSwitch,
		NgSwitchCase,

		MatButtonModule,
		BiggestStocksWidgetComponent,
		IssueFollowUpWidgetComponent,
		RtsWidgetComponent,
		OfflineComponent,
		LoadingSpinnerComponent
	]
})
export class DashboardComponent implements OnDestroy {
	saving = false;
	user: User;
	isLoading = false;
	isOffline = false;
	readonly INFORMATION = WidgetType.INFORMATION;

	keyInformationWidgets: Widget[] = [];

	informationEditMode = false;

	sortablejsOptions: Options = {
		disabled: true,
		onUpdate: () => {
			this.dashboardWidgetService.changeOrder(this.keyInformationWidgets);
		}
	};

	readonly BIGGEST_STOCKS_WIDGET = 'BiggestStocksWidget';
	readonly ISSUE_FOLLOW_UP_WIDGET = 'IssueFollowUpWidget';
	readonly RTS_WIDGET = 'RtsWidget';

	private readonly informationWidgetSub: Subscription;
	private dialogSub?: Subscription;
	constructor(
		private readonly userService: UserService,
		private readonly dashboardWidgetService: DashboardWidgetService,
		readonly locationService: LocationService,

		private readonly dialog: MatDialog
	) {
		this.isOffline = !navigator.onLine;
		this.user = this.userService.user;

		this.informationWidgetSub = this.dashboardWidgetService.subscribeToInformationWidget({
			next: value => {
				this.keyInformationWidgets = this.sortWidgets(value);
			}
		});
	}

	sortWidgets(widgets: Widget[]) {
		return widgets.sort((a, b) => a.priority - b.priority);
	}

	getSpanSize(widget: Widget) {
		return this.dashboardWidgetService.sizeWidget(widget.name);
	}

	ngOnDestroy() {
		if (this.informationEditMode) {
			this.dashboardWidgetService.cancelWidgets();
		}
		this.informationWidgetSub.unsubscribe();
		if (this.dialogSub) {
			this.dialogSub.unsubscribe();
		}
	}

	toggleOnInformationEditMode() {
		this.informationEditMode = true;
		this.sortablejsOptions = {
			...this.sortablejsOptions,
			disabled: false
		};
	}

	cancelCustomizationInformationWidgets() {
		this.informationEditMode = false;
		this.sortablejsOptions = {
			...this.sortablejsOptions,
			disabled: true
		};
		this.dashboardWidgetService.cancelWidgets();
	}

	async validateCustomizationInformationWidgets() {
		this.informationEditMode = false;
		this.sortablejsOptions = {
			...this.sortablejsOptions,
			disabled: true
		};
		this.saving = true;
		await this.dashboardWidgetService.validateWidgets().then(() => {
			this.saving = false;
		});
	}

	addNewWidget(widgetType: WidgetType) {
		const addDialog = this.dialog.open<WidgetAddComponent, DialogData, Widget | undefined>(WidgetAddComponent, {
			panelClass: 'add-widget-block-small-padding',
			data: {
				widgetType
			}
		});

		this.dialogSub = addDialog.afterClosed().subscribe(result => {
			if (!result) {
				return;
			}
			this.dashboardWidgetService.addWidget(result);
		});
	}

	changeWidget(widget: Widget) {
		const addDialog = this.dialog.open<WidgetAddComponent, DialogData, Widget | undefined>(WidgetAddComponent, {
			panelClass: 'add-widget-block-small-padding',
			data: {
				widgetType: widget.type,
				currentWidget: widget
			}
		});

		this.dialogSub = addDialog.afterClosed().subscribe(result => {
			if (!result) {
				return;
			}
			this.dashboardWidgetService.replaceWidget(widget, result);
		});
	}

	removeWidget(widget: Widget) {
		this.dashboardWidgetService.removeWidget(widget);
	}

	changePriority(widgetType: WidgetType) {
		if (WidgetType.INFORMATION !== widgetType) {
			return;
		}
		for (let i = 0; i < this.keyInformationWidgets.length; i++) {
			this.keyInformationWidgets[i].priority = i;
		}
	}
}
